import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { PopupCampaignAdminViewModel } from "../../api/generated/models/PopupCampaignAdminViewModel";
import { PopupCampaignStatus } from "../../api/generated/models/PopupCampaignStatus";
import { StatusBadge } from "./StatusBadge";
import { PopupCampaignStats } from "./PopupCampaignStats";

interface Props {
  campaign: PopupCampaignAdminViewModel;
  onClick: (campaign: PopupCampaignAdminViewModel) => void;
}

const fmt = (value: unknown) => (value ? new Date(value as string).toLocaleString() : null);

export const PopupCampaignCard: React.FC<Props> = ({ campaign, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: campaign.id!,
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const leftBorder = campaign.status === PopupCampaignStatus.ACTIVE ? "border-l-green-500" : "border-l-gray-300";

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg p-4 mb-3 shadow-sm border border-gray-200 border-l-4 ${leftBorder} cursor-pointer hover:shadow-md transition-shadow bg-white`}
      onClick={() => onClick(campaign)}
    >
      <div className="flex items-start gap-3">
        <div
          {...attributes}
          {...listeners}
          className="mt-1 flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
          onClick={(e) => e.stopPropagation()}
          title="Drag to change priority"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM13 14a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
          </svg>
        </div>

        {campaign.imageUrl && (
          <img
            src={campaign.imageUrl}
            alt=""
            className="w-12 h-[60px] rounded object-cover border border-gray-200 flex-shrink-0"
          />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900">{campaign.name || "Untitled"}</h3>
            <StatusBadge status={campaign.status} />
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {fmt(campaign.startAt) ? `From: ${fmt(campaign.startAt)}` : "No start"}
            {" · "}
            {fmt(campaign.endAt) ? `Until: ${fmt(campaign.endAt)}` : "No end"}
          </p>
          <div className="mt-3">
            <PopupCampaignStats stats={campaign.stats} compact />
          </div>
        </div>
      </div>
    </div>
  );
};
